/**
 * PricingCalculatorStickyPanel
 *
 * A condensed, sticky summary panel for the pricing calculator.
 * Shows real-time totals per section with expandable details.
 *
 * Features:
 * - Fixed position on right side (desktop)
 * - Shows real-time totals per section
 * - Highlights changes when values update
 * - Collapse/expand section details
 * - Export/Copy quote button
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx - Main form
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  Copy as CopyIcon,
  Building2 as BuildingIcon,
  CreditCard as CreditCardIcon,
  Server as ServerIcon,
  Cloud as CloudIcon,
  Puzzle as PuzzleIcon,
  Blocks as BlocksIcon,
  Calculator as CalculatorIcon,
  RotateCcw as ResetIcon,
  Share2 as ShareIcon,
} from 'lucide-react';
import {
  PricingCalculatorFormValues,
  PricingCalculatorSummary,
  TenantType,
  isDedicatedTenant,
  computeSectionSubtotals,
} from '../../types/pricingCalculator';

interface SectionSummaryProps {
  title: string;
  icon: React.ReactNode;
  subtotal: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  hasSelections?: boolean;
}

const SectionSummary: React.FC<SectionSummaryProps> = ({
  title,
  icon,
  subtotal,
  isExpanded,
  onToggle,
  children,
  hasSelections = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  // Stable id targeted by `aria-controls` so assistive tech can resolve the
  // header's controlled element regardless of expanded state.
  const contentId = `sticky-section-${title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1,
          px: 1.5,
          borderRadius: '8px',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: isDark
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.primary.main, 0.04),
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '-2px',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              color: hasSelections ? theme.palette.primary.main : theme.palette.text.secondary,
              display: 'flex',
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              fontSize: '0.85rem',
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: subtotal > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
              fontSize: '0.85rem',
            }}
          >
            $
            {subtotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
          {/* Decorative chevron — the parent Box is the actual button. Plain
           * Box (not IconButton) avoids a button-inside-button antipattern
           * and keeps the parent as the sole focusable element. */}
          <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', p: 0.25 }}>
            {isExpanded ? <ChevronUpIcon size={14} /> : <ChevronDownIcon size={14} />}
          </Box>
        </Box>
      </Box>
      {/* Container is always rendered with the id targeted by `aria-controls`;
       * visibility is toggled via `hidden`. See PricingCalculatorSection for
       * the library-bug context. */}
      <Box
        id={contentId}
        hidden={!isExpanded}
        sx={{
          pl: 4,
          pr: 1.5,
          py: 1,
          fontSize: '0.75rem',
          color: theme.palette.text.secondary,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface DetailRowProps {
  label: string;
  value: string | number;
  highlight?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, highlight = false }) => {
  const theme = useTheme();
  const formattedValue =
    typeof value === 'number'
      ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : value;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: 0.25,
        fontSize: '0.75rem',
      }}
    >
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: highlight ? 600 : 400,
          color: highlight ? theme.palette.primary.main : theme.palette.text.primary,
        }}
      >
        {formattedValue}
      </Typography>
    </Box>
  );
};

export interface PricingCalculatorStickyPanelProps {
  values: PricingCalculatorFormValues;
  summary: PricingCalculatorSummary | null;
  /** Callback to reset the form to defaults */
  onReset?: () => void;
  /** Function to generate a shareable URL with current form values */
  getShareableUrl?: () => string;
}

type SectionSummariesPropsType = ReturnType<typeof computeSectionSubtotals> & {
  values: PricingCalculatorFormValues;
  summary: PricingCalculatorSummary;
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;
};

/** Per-section collapsible summaries, mirroring the form's own section order. */
const SectionSummaries = ({
  values,
  summary,
  expandedSections,
  toggleSection,
  memberPricingSubtotal,
  platformSubtotal,
  serverSubtotal,
  govCloudSubtotal,
  addonsSubtotal,
  integrationsSubtotal,
}: SectionSummariesPropsType) => (
  <Box sx={{ p: 1.5 }}>
    {/* Member Pricing — mirrors the Form's "Member Pricing" section */}
    <SectionSummary
      title="Member Pricing"
      icon={<BuildingIcon size={16} />}
      subtotal={memberPricingSubtotal}
      isExpanded={expandedSections.memberPricing}
      onToggle={() => toggleSection('memberPricing')}
      hasSelections={values.numberOfMembers > 0}
    >
      <DetailRow
        label={`${values.numberOfMembers} members`}
        value={summary.totalMemberCostYearly}
      />
      {values.numberOfInternalContractors > 0 && (
        <DetailRow
          label={`${values.numberOfInternalContractors} contractors`}
          value={summary.internalContractorsTotal}
        />
      )}
      {values.numberOfExternalContractors > 0 && (
        <DetailRow
          label={`${values.numberOfExternalContractors} ext. contractors`}
          value={summary.externalContractorsTotal}
        />
      )}
      {values.numberOfGuests > 0 && (
        <DetailRow label={`${values.numberOfGuests} guests`} value={summary.guestsTotal} />
      )}
    </SectionSummary>

    {/* Platform & Onboarding — mirrors the Form's "Platform & Onboarding" section */}
    <SectionSummary
      title="Platform & Onboarding"
      icon={<CreditCardIcon size={16} />}
      subtotal={platformSubtotal}
      isExpanded={expandedSections.platform}
      onToggle={() => toggleSection('platform')}
      hasSelections={platformSubtotal > 0}
    >
      <DetailRow label="Platform fee" value={summary.platformFeeYearly} />
      {summary.onboardingFeeAfterDiscount > 0 && (
        <DetailRow label="Onboarding" value={summary.onboardingFeeAfterDiscount} />
      )}
      {summary.additionalEnterpriseSupportCost > 0 && (
        <DetailRow
          label={`Enterprise support (${summary.additionalEnterpriseSupportHours}h)`}
          value={summary.additionalEnterpriseSupportCost}
        />
      )}
    </SectionSummary>

    {/* Implementation Options */}
    <SectionSummary
      title="Implementation Options"
      icon={<ServerIcon size={16} />}
      subtotal={serverSubtotal}
      isExpanded={expandedSections.server}
      onToggle={() => toggleSection('server')}
      hasSelections={serverSubtotal > 0}
    >
      {summary.directDatabaseFee > 0 && (
        <DetailRow label="Direct Database" value={summary.directDatabaseFee} />
      )}
      {summary.isolatedDatabaseFeeYearly > 0 && (
        <DetailRow label="Isolated Database" value={summary.isolatedDatabaseFeeYearly} />
      )}
      {summary.tenantType === TenantType.SINGLE && (
        <DetailRow label="Single Tenant" value={summary.tenantFee} />
      )}
      {summary.tenantType === TenantType.ENTERPRISE && (
        <DetailRow label="Enterprise Tenant" value={summary.tenantFee} />
      )}
      {summary.isolatedAi && !isDedicatedTenant(summary.tenantType) && (
        <DetailRow label="Isolated AI" value={summary.isolatedAiFee} />
      )}
      {serverSubtotal === 0 && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          No server upgrades selected
        </Typography>
      )}
    </SectionSummary>

    {/* Gov Cloud */}
    {summary.govCloud && (
      <SectionSummary
        title="Gov Cloud"
        icon={<CloudIcon size={16} />}
        subtotal={govCloudSubtotal}
        isExpanded={expandedSections.govCloud}
        onToggle={() => toggleSection('govCloud')}
        hasSelections={true}
      >
        <DetailRow label="20% surcharge" value={govCloudSubtotal} />
      </SectionSummary>
    )}

    {/* Add-ons */}
    <SectionSummary
      title="Add-ons"
      icon={<BlocksIcon size={16} />}
      subtotal={addonsSubtotal}
      isExpanded={expandedSections.addons}
      onToggle={() => toggleSection('addons')}
      hasSelections={addonsSubtotal > 0}
    >
      {summary.securityPackageSso && <DetailRow label="SSO" value={summary.ssoTotalYearly} />}
      {summary.aiForecasting && (
        <DetailRow label="AI Forecasting" value={summary.aiForecastingFeeAfterDiscount} />
      )}
      {summary.nonPeopleResources && (
        <DetailRow
          label="Non-People Resources"
          value={summary.nonPeopleResourcesFeeAfterDiscount}
        />
      )}
      {summary.profileBuilding && (
        <DetailRow label="Profile Building" value={summary.profileBuildingFeeAfterDiscount} />
      )}
      {summary.skillsTagging && (
        <DetailRow label="Skills Tagging" value={summary.skillsTaggingFeeAfterDiscount} />
      )}
      {summary.solutionsSupport && (
        <DetailRow label="Solutions Support" value={summary.solutionsSupportFee} />
      )}
      {summary.enableWhiteLabel && (
        <DetailRow label="White-Label" value={summary.whiteLabelAdditionalCost} />
      )}
      {addonsSubtotal === 0 && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          No add-ons selected
        </Typography>
      )}
    </SectionSummary>

    {/* Integrations */}
    <SectionSummary
      title="Integrations"
      icon={<PuzzleIcon size={16} />}
      subtotal={integrationsSubtotal}
      isExpanded={expandedSections.integrations}
      onToggle={() => toggleSection('integrations')}
      hasSelections={values.selectedIntegrations.length > 0}
    >
      {values.selectedIntegrations.length > 0 ? (
        <>
          <DetailRow
            label={`${values.selectedIntegrations.length} integration(s)`}
            value={summary.integrationRecurringFeeAfterDiscount}
          />
          {summary.integrationSetupFeeAfterDiscount > 0 && (
            <DetailRow label="Setup fees" value={summary.integrationSetupFeeAfterDiscount} />
          )}
        </>
      ) : (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          No integrations selected
        </Typography>
      )}
    </SectionSummary>
  </Box>
);

export const PricingCalculatorStickyPanel: React.FC<PricingCalculatorStickyPanelProps> = ({
  values,
  summary,
  onReset,
  getShareableUrl,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    memberPricing: false,
    platform: false,
    server: false,
    govCloud: false,
    addons: false,
    integrations: false,
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('Quote copied to clipboard!');

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleCopyQuote = useCallback(async () => {
    if (!summary) return;

    const quoteText = `
Pricing Quote for ${values.companyName || 'Customer'}
Generated: ${new Date().toLocaleDateString()}
Duration: ${values.subscriptionDuration} months

Members: ${values.numberOfMembers} @ $${values.pricePerMember}/mo
Member Cost: $${summary.totalMemberCostYearly.toLocaleString()}
Platform Fee: $${summary.platformFeeYearly.toLocaleString()}

One-time Fees: $${summary.oneTimeFeeSubtotal.toLocaleString()}
Recurring Fees: $${summary.recurringFeeSubtotal.toLocaleString()}

TOTAL: $${summary.totalCost.toLocaleString()}
    `.trim();

    try {
      await navigator.clipboard.writeText(quoteText);
      setSnackbarMessage('Quote copied to clipboard!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to copy quote to clipboard:', error);
    }
  }, [summary, values]);

  const handleCopyShareLink = useCallback(async () => {
    if (!getShareableUrl) return;

    try {
      const url = getShareableUrl();
      await navigator.clipboard.writeText(url);
      setSnackbarMessage('Share link copied to clipboard!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to copy share link to clipboard:', error);
    }
  }, [getShareableUrl]);

  if (!summary) {
    return (
      <Box
        sx={{
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.8)
            : theme.palette.background.paper,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, isDark ? 0.1 : 0.2),
          p: 3,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalculatorIcon size={20} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Quote Summary
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Fill in the form to see pricing summary
        </Typography>
      </Box>
    );
  }

  // Subtotals are derived from the same shared helper the Form uses, so the
  // top (Form) and bottom (Sticky) views can never disagree on the same line.
  const {
    memberPricingSubtotal,
    platformSubtotal,
    serverSubtotal,
    govCloudSubtotal,
    addonsSubtotal,
    integrationsSubtotal,
  } = computeSectionSubtotals(summary);

  // Calculate total savings from discounts
  const calculateTotalSavings = () => {
    let savings = 0;

    // Member pricing savings
    const memberSavings =
      (summary.originalPricePerMember - summary.discountedPricePerMember) *
      values.numberOfMembers *
      values.subscriptionDuration;
    savings += memberSavings;

    // Platform fee savings
    const platformFeeSavings =
      (summary.platformFeeMonthly - summary.platformFeeAfterDiscountMonthly) *
      values.subscriptionDuration;
    savings += platformFeeSavings;

    // Onboarding fee savings
    savings += summary.onboardingFee - summary.onboardingFeeAfterDiscount;

    // SSO savings
    if (summary.securityPackageSso) {
      savings += summary.ssoTotalYearlyBeforeDiscount - summary.ssoTotalYearly;
    }

    // Dedicated tenant savings (Single or Enterprise)
    if (isDedicatedTenant(summary.tenantType)) {
      savings += summary.tenantFeeBeforeDiscount - summary.tenantFee;
    }

    // Direct Database savings
    if (summary.directDatabase) {
      savings += summary.directDatabaseFeeBeforeDiscount - summary.directDatabaseFee;
    }

    // Isolated Database savings
    if (summary.isolatedDatabase) {
      savings +=
        summary.isolatedDatabaseFeeYearlyBeforeDiscount - summary.isolatedDatabaseFeeYearly;
    }

    // AI Forecasting savings
    if (summary.aiForecasting) {
      savings += summary.aiForecastingFee - summary.aiForecastingFeeAfterDiscount;
    }

    // Non-People Resources savings
    if (summary.nonPeopleResources) {
      savings += summary.nonPeopleResourcesFee - summary.nonPeopleResourcesFeeAfterDiscount;
    }

    // Profile Building savings
    if (summary.profileBuilding) {
      savings += summary.profileBuildingFee - summary.profileBuildingFeeAfterDiscount;
    }

    // Skills Tagging savings
    if (summary.skillsTagging) {
      savings += summary.skillsTaggingFee - summary.skillsTaggingFeeAfterDiscount;
    }

    // Gov Cloud savings
    if (summary.govCloud) {
      savings += summary.govCloudFee - summary.govCloudFeeAfterDiscount;
    }

    // Integration setup fee savings
    savings += summary.integrationSetupFee - summary.integrationSetupFeeAfterDiscount;

    // Integration recurring fee savings
    savings += summary.integrationRecurringFee - summary.integrationRecurringFeeAfterDiscount;

    return savings;
  };

  const totalSavings = calculateTotalSavings();

  // Calculate configuration summary for Sales/CSM
  const getConfigSummary = () => {
    const features: string[] = [];

    if (values.securityPackageSso) features.push('SSO');
    if (values.tenantType === TenantType.SINGLE) features.push('Single Tenant');
    else if (values.tenantType === TenantType.ENTERPRISE) features.push('Enterprise Tenant');
    else if (values.directDatabaseIntegration) features.push('Direct DB');
    else if (values.isolatedDatabase) features.push('Isolated DB');
    if (values.govCloud) features.push('Gov Cloud');
    if (values.aiForecasting) features.push('AI Forecasting');
    if (values.nonPeopleResources) features.push('NPR');
    if (values.profileBuilding) features.push('Profile Building');
    if (values.skillsTagging) features.push('Skills Tagging');
    if (values.enableWhiteLabel) features.push('White Label');
    if (values.solutionsSupport) features.push('Solutions Support');

    const integrationCount = values.selectedIntegrations?.length || 0;

    return { features, integrationCount };
  };

  const configSummary = getConfigSummary();
  const isCompanyNameMissing = !values.companyName.trim();

  return (
    <Box
      sx={{
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.85)
          : theme.palette.background.paper,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, isDark ? 0.1 : 0.2),
        backdropFilter: 'blur(16px)',
        boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          backgroundColor: isDark
            ? alpha(theme.palette.primary.main, 0.1)
            : alpha(theme.palette.primary.main, 0.05),
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalculatorIcon size={20} color={theme.palette.primary.main} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Quote Summary
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {onReset && (
              <Tooltip title="Reset to defaults">
                <IconButton size="small" onClick={onReset}>
                  <ResetIcon size={16} />
                </IconButton>
              </Tooltip>
            )}
            {getShareableUrl && (
              <Tooltip title="Copy shareable link">
                <IconButton size="small" onClick={handleCopyShareLink}>
                  <ShareIcon size={16} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Copy quote to clipboard">
              <IconButton size="small" onClick={handleCopyQuote}>
                <CopyIcon size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {values.companyName && (
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: theme.palette.text.secondary, fontSize: '0.8rem' }}
          >
            {values.companyName} • {values.subscriptionDuration} months
          </Typography>
        )}

        {/* Configuration Summary - Shows what's enabled */}
        {(configSummary.features.length > 0 || configSummary.integrationCount > 0) && (
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {configSummary.features.map((feature) => (
              <Chip
                key={feature}
                label={feature}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: theme.palette.text.secondary,
                }}
              />
            ))}
            {configSummary.integrationCount > 0 && (
              <Chip
                label={`${configSummary.integrationCount} integration${configSummary.integrationCount > 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  borderColor: alpha(theme.palette.info.main, 0.3),
                  color: theme.palette.text.secondary,
                }}
              />
            )}
          </Box>
        )}

        {/* Missing Company Name Warning */}
        {isCompanyNameMissing && (
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              display: 'block',
              color: theme.palette.warning.main,
              fontSize: '0.7rem',
            }}
          >
            Enter company name for quote
          </Typography>
        )}
      </Box>

      {/* Section Summaries */}
      <SectionSummaries
        values={values}
        summary={summary}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        memberPricingSubtotal={memberPricingSubtotal}
        platformSubtotal={platformSubtotal}
        serverSubtotal={serverSubtotal}
        govCloudSubtotal={govCloudSubtotal}
        addonsSubtotal={addonsSubtotal}
        integrationsSubtotal={integrationsSubtotal}
      />

      <Divider sx={{ opacity: 0.5 }} />

      {/* Totals */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            One-time fees
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            $
            {summary.oneTimeFeeSubtotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Recurring fees
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            $
            {summary.recurringFeeSubtotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>

        {/* Savings Display */}
        {totalSavings > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
              py: 1,
              px: 1.5,
              backgroundColor: isDark
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.success.main, 0.08),
              borderRadius: '8px',
              border: '1px solid',
              borderColor: alpha(theme.palette.success.main, 0.2),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.success.main,
              }}
            >
              You save
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.success.main,
              }}
            >
              $
              {totalSavings.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Box>
        )}

        {/* Grand Total */}
        <Box
          sx={{
            backgroundColor: isDark
              ? alpha(theme.palette.primary.main, 0.15)
              : alpha(theme.palette.primary.main, 0.08),
            borderRadius: '8px',
            p: 1.5,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.65rem',
            }}
          >
            Total Quote
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: '1.5rem',
            }}
          >
            $
            {summary.totalCost.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
      </Box>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PricingCalculatorStickyPanel;
